import { useEffect, useRef } from 'react';
import { useStreamSettingsStore } from '../../stores/stream-screen-settings.store';
import { useStreamUserDefinedRange } from '../../stores/stream-screen.store';
import { useStreamSelection } from '../../stores/useStreamSelection';

/**
 * Hook that clears the selection whenever the stream range changes,
 * but only if the keepSelectionOnRangeChange setting is false.
 * This ensures that when a user changes the range, the selection is automatically cleared
 * unless they've configured it to be kept.
 */
export function useClearSelectionOnRangeChange() {
  const range = useStreamUserDefinedRange();
  const { clearSelection } = useStreamSelection();
  const previousRange = useRef<{ from: number; to: number } | null>(null);

  const keepSelectionOnRangeChange = useStreamSettingsStore(
    (state) => state.keepSelectionOnRangeChange,
  );

  useEffect(() => {
    // Skip if we should keep selection on range change
    if (keepSelectionOnRangeChange) {
      // Update the ref even if we're not clearing, so we track the current range
      previousRange.current = range.isLoading ? null : { from: range.from, to: range.to };
      return;
    }

    // Don't track range while still loading; otherwise we treat initial (0,0) -> real range as a "change" and clear URL-derived selection
    if (range.isLoading) {
      previousRange.current = null;
      return;
    }

    // Skip on initial mount (when previousRange is null) — first loaded range is not a "change"
    if (previousRange.current === null) {
      previousRange.current = { from: range.from, to: range.to };
      return;
    }

    // Check if range has changed
    const rangeChanged =
      previousRange.current.from !== range.from || previousRange.current.to !== range.to;

    // If range changed, clear the selection
    if (rangeChanged) {
      clearSelection();
    }

    // Update the ref for the next render
    previousRange.current = { from: range.from, to: range.to };
  }, [range, keepSelectionOnRangeChange, clearSelection]);
}
